import React, { useState, useEffect, useRef, useCallback } from 'react';

interface SlidePresentationProps {
  children: React.ReactNode[];
  labels: string[];
  slideStyles?: Record<number, React.CSSProperties>;
  slideClassNames?: Record<number, string>;
}

/**
 * SlidePresentation - Full-screen slide engine with navigation.
 * Renders each child as a 16:9 slide with keyboard, wheel, and touch navigation.
 */
const SlidePresentation: React.FC<SlidePresentationProps> = ({
  children,
  labels,
  slideStyles = {},
  slideClassNames = {},
}) => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const wheelLock = useRef(false);
  const touchStartY = useRef<number | null>(null);
  const total = children.length;

  const isMobile = () => window.innerWidth <= 768;

  // Fix10: Add/remove .scrollable class on .si elements
  useEffect(() => {
    const updateScrollable = () => {
      if (!isMobile()) return;
      document.querySelectorAll('.si').forEach((el) => {
        const htmlEl = el as HTMLElement;
        const isScrollable = htmlEl.scrollHeight > htmlEl.clientHeight + 2;
        if (isScrollable) {
          htmlEl.classList.add('scrollable');
        } else {
          htmlEl.classList.remove('scrollable');
        }
      });
    };
    updateScrollable();
    window.addEventListener('resize', updateScrollable);
    // Re-check after slide change
    const timer = setTimeout(updateScrollable, 300);
    return () => {
      window.removeEventListener('resize', updateScrollable);
      clearTimeout(timer);
    };
  }, [currentSlide]);

  // Check if the active .si is scrolled to top/bottom
  const canNavigate = useCallback((direction: number): boolean => {
    if (!isMobile()) return true; // Desktop: always navigate
    const activeSi = document.querySelector('.slide.active .si') as HTMLElement | null;
    if (!activeSi) return true;
    const { scrollTop, scrollHeight, clientHeight } = activeSi;
    const isScrollable = scrollHeight > clientHeight + 2; // 2px tolerance
    if (!isScrollable) return true; // Not scrollable = navigate freely
    if (direction > 0) {
      // Going next: must be at bottom
      return scrollTop + clientHeight >= scrollHeight - 5;
    } else {
      // Going prev: must be at top
      return scrollTop <= 5;
    }
  }, []);

  const go = useCallback(
    (delta: number) => {
      if (!canNavigate(delta)) return; // Block if not scrolled to edge on mobile
      setCurrentSlide((prev) => {
        const next = prev + delta;
        if (next < 0 || next >= total) return prev;
        return next;
      });
    },
    [total, canNavigate]
  );

  // Keyboard navigation
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'ArrowDown' || e.key === 'ArrowRight') {
        e.preventDefault();
        go(1);
      }
      if (e.key === 'ArrowUp' || e.key === 'ArrowLeft') {
        e.preventDefault();
        go(-1);
      }
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [go]);

  // Mouse wheel navigation (debounced 600ms, mobile: only at scroll edges)
  useEffect(() => {
    const handler = (e: WheelEvent) => {
      if (wheelLock.current) return;
      if (Math.abs(e.deltaY) > 40) {
        const direction = e.deltaY > 0 ? 1 : -1;
        if (!canNavigate(direction)) return; // Let the .si scroll instead
        wheelLock.current = true;
        go(direction);
        setTimeout(() => {
          wheelLock.current = false;
        }, 600);
      }
    };
    document.addEventListener('wheel', handler, { passive: true });
    return () => document.removeEventListener('wheel', handler);
  }, [go, canNavigate]);

  // Touch swipe navigation (60px threshold, mobile: only at scroll edges)
  useEffect(() => {
    const onTouchStart = (e: TouchEvent) => {
      touchStartY.current = e.touches[0].clientY;
    };
    const onTouchEnd = (e: TouchEvent) => {
      if (touchStartY.current === null) return;
      const diff = touchStartY.current - e.changedTouches[0].clientY;
      touchStartY.current = null;
      if (Math.abs(diff) > 60) {
        const direction = diff > 0 ? 1 : -1;
        if (!canNavigate(direction)) return; // Let the .si scroll instead
        go(direction);
      }
    };
    document.addEventListener('touchstart', onTouchStart);
    document.addEventListener('touchend', onTouchEnd);
    return () => {
      document.removeEventListener('touchstart', onTouchStart);
      document.removeEventListener('touchend', onTouchEnd);
    };
  }, [go]);

  return (
    <div
      style={{
        fontFamily: '"Inter", "Noto Sans JP", sans-serif',
        fontWeight: 500,
        color: '#1D1926',
        background: '#F6F5F0',
        margin: 0,
        overflow: 'hidden',
        height: '100dvh',
        WebkitFontSmoothing: 'antialiased',
        position: 'relative',
      }}
    >
      {/* Slides */}
      {React.Children.map(children, (child, i) => {
        const isActive = i === currentSlide;
        const extraClass = slideClassNames[i] || '';
        const extraStyle = slideStyles[i] || {};

        return (
          <div
            className={`slide ${isActive ? 'active' : ''} ${extraClass}`}
            style={{
              alignItems: 'flex-start',
              ...extraStyle,
            }}
          >
            <div className="si" style={{ position: 'relative' }}>
              {/* Tasky logo */}
              <img
                src={
                  extraClass.includes('slide-cta')
                    ? '/lp/logos/logo-white.png'
                    : '/lp/logos/logo-dark.png'
                }
                className="slide-logo"
                alt="Tasky"
              />
              {child}
            </div>
          </div>
        );
      })}

      {/* Bottom navigation bar */}
      <div className="bnav">
        {/* Dots */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
          {Array.from({ length: total }).map((_, i) => (
            <div
              key={i}
              className={`dot ${i === currentSlide ? 'active' : i < currentSlide ? 'visited' : ''}`}
              onClick={() => setCurrentSlide(i)}
            />
          ))}
        </div>

        {/* Label */}
        <p
          className="bnav-label"
          style={{
            fontSize: 12,
            color: 'rgba(29,25,38,0.4)',
            fontWeight: 500,
            margin: 0,
          }}
        >
          {labels[currentSlide] || ''}
        </p>

        {/* Arrow buttons + counter */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
          <button
            className="nb"
            onClick={() => go(-1)}
            disabled={currentSlide === 0}
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#1D1926" strokeWidth="2">
              <polyline points="18 15 12 9 6 15" />
            </svg>
          </button>
          <span
            style={{
              fontSize: 11,
              color: 'rgba(29,25,38,0.4)',
              width: 40,
              textAlign: 'center',
              fontFamily: 'Inter, sans-serif',
              fontVariantNumeric: 'tabular-nums',
            }}
          >
            {currentSlide + 1}/{total}
          </span>
          <button
            className="nb"
            onClick={() => go(1)}
            disabled={currentSlide === total - 1}
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#1D1926" strokeWidth="2">
              <polyline points="6 9 12 15 18 9" />
            </svg>
          </button>
        </div>
      </div>

      {/* PDF download button */}
      <button className="pdf-btn" onClick={() => window.print()}>
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4" />
          <polyline points="7 10 12 15 17 10" />
          <line x1="12" y1="15" x2="12" y2="3" />
        </svg>
        PDF
      </button>
    </div>
  );
};

export default SlidePresentation;
